import React, { useEffect, useState, useMemo } from 'react';
import { useAtom } from 'jotai';
import { Card, Switch, Progress, Tag, Tooltip, Alert, Select } from 'antd';
import { Mic, MicOff, WifiOff, AudioLines, Globe } from 'lucide-react';
import { 
  deliveryCoachingAtom, 
  isCoachingEnabledAtom,
  fillerWordRateAtom,
  updateCoachingMetricsAtom
} from '../../atoms/aiAtoms';
import { getDeliveryCoachingService, SUPPORTED_LANGUAGES } from '../../services/deliveryCoachingService';
import './CoachingOverlay.scss';

interface CoachingOverlayProps {
  isRecording: boolean;
  isPaused?: boolean;
  isMicEnabled?: boolean;
  position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
}

export const CoachingOverlay: React.FC<CoachingOverlayProps> = ({ 
  isRecording,
  isPaused = false,
  isMicEnabled = true,
  position = 'top-right'
}) => {
  const [coaching, ] = useAtom(deliveryCoachingAtom);
  const [isEnabled, setIsEnabled] = useAtom(isCoachingEnabledAtom);
  const [fillerRate] = useAtom(fillerWordRateAtom);
  const [, updateMetrics] = useAtom(updateCoachingMetricsAtom);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const coachingService = useMemo(() => getDeliveryCoachingService(), []);
  const isSupported = useMemo(() => coachingService.isSupported(), [coachingService]);

  // Track if service was started (to handle pause/resume properly)
  const [serviceStarted, setServiceStarted] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');

  // Handle language change
  const handleLanguageChange = (langCode: string) => {
    setSelectedLanguage(langCode);
    coachingService.setLanguage(langCode);
  };

  // Main effect: Start/stop based on isRecording only (not isPaused)
  useEffect(() => {
    if (!isSupported) return;
    
    // Set awaiting status when enabled but not recording or mic disabled
    if (isEnabled && (!isRecording || !isMicEnabled)) {
      updateMetrics({ status: 'awaiting', isActive: false });
      setServiceStarted(false);
      return;
    }
    
    if (isRecording && isEnabled && isMicEnabled) {
      const startCoaching = async () => {
        try {
          await coachingService.start((metrics) => {
            updateMetrics({
              currentPace: metrics.currentPace,
              paceStatus: metrics.paceStatus,
              fillerWordCount: metrics.fillerWordCount,
              totalWords: metrics.totalWords,
              suggestions: metrics.suggestions,
              transcript: metrics.transcript,
              status: metrics.status,
              isActive: true
            });
          });
          setServiceStarted(true);
          setPermissionError(null);
          
          // If starting while paused, immediately pause
          if (isPaused) {
            coachingService.pause();
          }
        } catch (error: any) {
          console.error('[CoachingOverlay] Failed to start coaching:', error);
          setPermissionError(error.message || 'Failed to start delivery coaching');
          updateMetrics({ isActive: false, status: 'idle' });
          setIsEnabled(false);
          setServiceStarted(false);
        }
      };

      startCoaching();

      return () => {
        coachingService.stop();
        updateMetrics({ isActive: false, status: 'idle' });
        setServiceStarted(false);
      };
    } else if (!isRecording && coaching.isActive) {
      coachingService.stop();
      updateMetrics({ isActive: false, status: 'idle' });
      setServiceStarted(false);
    }
  }, [isRecording, isEnabled, isMicEnabled, coachingService, updateMetrics, coaching.isActive, setIsEnabled, isSupported]);

  // Separate effect for pause/resume (doesn't trigger cleanup/restart)
  useEffect(() => {
    if (!isSupported || !isEnabled || !isRecording || !isMicEnabled || !serviceStarted) return;
    
    if (isPaused) {
      coachingService.pause();
    } else {
      coachingService.resume();
    }
  }, [isPaused, isSupported, isEnabled, isRecording, isMicEnabled, serviceStarted, coachingService]);

  // Hide component entirely if browser doesn't support speech recognition
  if (!isSupported) {
    return null;
  }

  if (!isEnabled) {
    return (
      <div className={`coaching-overlay minimal ${position}`}>
        <Card size="small" className="coaching-toggle-card">
          <div className="toggle-section">
            <span className="toggle-label">Delivery Coaching</span>
            <Switch 
              checked={false}
              onChange={setIsEnabled}
            />
          </div>
          {permissionError && (
            <Alert
              message={permissionError}
              type="error"
              closable
              onClose={() => setPermissionError(null)}
              style={{ marginTop: 8 }}
              showIcon
            />
          )}
        </Card>
      </div>
    );
  }

  const getPaceColor = () => {
    if (coaching.paceStatus === 'good') return '#52c41a';
    if (coaching.paceStatus === 'too-fast') return '#ff4d4f';
    return '#faad14';
  };

  const getPacePercent = () => {
    if (!coaching.currentPace) return 0;
    const normalized = ((coaching.currentPace - 100) / 100) * 100;
    return Math.max(0, Math.min(100, normalized));
  };

  const getStatusIndicator = () => {
    const status = coaching.status;
    
    // Show mic disabled message if mic is off
    if (!isMicEnabled) {
      return (
        <div className="status-indicator awaiting">
          <MicOff size={14} />
          <span>Enable microphone to use coaching</span>
        </div>
      );
    }
    
    switch (status) {
      case 'awaiting':
        return (
          <div className="status-indicator awaiting">
            <MicOff size={14} />
            <span>{isRecording && isPaused ? 'Recording paused' : 'Start recording to begin'}</span>
          </div>
        );
      case 'listening':
        return (
          <div className="status-indicator listening">
            <Mic size={14} />
            <span>Listening for speech...</span>
          </div>
        );
      case 'processing':
        return (
          <div className="status-indicator processing">
            <AudioLines size={14} />
            <span>Analyzing speech</span>
          </div>
        );
      case 'networkError':
        return (
          <div className="status-indicator network-error">
            <WifiOff size={14} />
            <span>Network issue - reconnecting...</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`coaching-overlay ${position} ${coaching.isActive ? 'active' : ''}`}>
      <Card 
        size="small" 
        className="coaching-card"
        title={
          <div className="coaching-header">
            <span>Delivery Coach</span>
            <Switch 
              checked={isEnabled}
              onChange={setIsEnabled}
              size="small"
            />
          </div>
        }
      >
        {/* Language Selector */}
        <div className="language-selector">
          <Globe size={14} className="language-icon" />
          <Select
            value={selectedLanguage}
            onChange={handleLanguageChange}
            size="small"
            style={{ width: 140 }}
            disabled={coaching.isActive}
            options={SUPPORTED_LANGUAGES.map(lang => ({
              value: lang.code,
              label: lang.name
            }))}
          />
        </div>

        {/* Pace Meter */}
        <div className="metric-section">
          <div className="metric-header">
            <span className="metric-label">Speaking Pace</span>
            <Tooltip title={`${coaching.currentPace} words per minute`}>
              <Tag color={getPaceColor()} className="pace-tag">
                {coaching.currentPace} WPM
              </Tag>
            </Tooltip>
          </div>
          <Progress 
            percent={getPacePercent()} 
            strokeColor={getPaceColor()}
            showInfo={false}
            size="small"
          />
          <div className="pace-range">
            <span>100</span>
            <span className="ideal-range">120-180 (ideal)</span>
            <span>200</span>
          </div>
        </div>

        {/* Filler Words */}
        <div className="metric-section">
          <div className="metric-header">
            <span className="metric-label">Filler Words</span>
            <Tag color={fillerRate > 5 ? 'orange' : 'green'} className="filler-tag">
              {coaching.fillerWordCount} ({fillerRate}%)
            </Tag>
          </div>
          <div className="filler-info">
            {coaching.totalWords} words spoken
          </div>
        </div>

        {/* Suggestions */}
        {coaching.suggestions.length > 0 && (
          <div className="suggestions-section">
            <div className="suggestions-label">Tips:</div>
            <ul className="suggestions-list">
              {coaching.suggestions.map((suggestion, index) => (
                <li key={index} className="suggestion-item">
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Status Indicator */}
        {getStatusIndicator()}
      </Card>
    </div>
  );
};
